import { useCallback, useState } from 'react';
import type { FilesApi, UploadErrorContext } from './types';

export interface FileUploadState {
    showUploadZone: boolean;
    isDragOver: boolean;
    isUploading: boolean;
    uploadError: string | null;
    uploadSuccess: boolean;
    stagedFiles: File[];
    uploadProgress: number;
    currentUploadingFile: string;
    uploadedCount: number;
    totalUploadCount: number;
}

export interface FileUploadActions {
    toggleUploadZone: () => void;
    setIsDragOver: React.Dispatch<React.SetStateAction<boolean>>;
    handleDrop: (e: React.DragEvent) => void;
    handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    setStagedFiles: React.Dispatch<React.SetStateAction<File[]>>;
    clearUploadError: () => void;
    uploadFiles: () => Promise<void>;
}

interface UseFileUploadOptions {
    patientId: string;
    refreshAssets: () => Promise<void>;
    api: FilesApi;
    /** Observability seam — called once per failed file upload. */
    onUploadError?: (error: unknown, ctx: UploadErrorContext) => void;
}

export function useFileUpload({
    patientId,
    refreshAssets,
    api,
    onUploadError
}: UseFileUploadOptions): FileUploadState & FileUploadActions {
    const [showUploadZone, setShowUploadZone] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [stagedFiles, setStagedFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentUploadingFile, setCurrentUploadingFile] = useState('');
    const [uploadedCount, setUploadedCount] = useState(0);
    const [totalUploadCount, setTotalUploadCount] = useState(0);

    const toggleUploadZone = useCallback(() => {
        setShowUploadZone((prev) => {
            if (prev) {
                setStagedFiles([]);
                setUploadError(null);
            }
            return !prev;
        });
    }, []);

    const addFilesToStaging = useCallback((files: File[]) => {
        setStagedFiles((prev) => [...prev, ...files]);
        setUploadError(null);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const files = Array.from(e.dataTransfer.files);
            if (files.length > 0) addFilesToStaging(files);
        },
        [addFilesToStaging]
    );

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) addFilesToStaging(files);
            e.target.value = '';
        },
        [addFilesToStaging]
    );

    const clearUploadError = useCallback(() => setUploadError(null), []);

    const uploadFiles = useCallback(async () => {
        if (stagedFiles.length === 0) return;
        setIsUploading(true);
        setUploadError(null);
        setUploadSuccess(false);
        setUploadProgress(0);
        setCurrentUploadingFile('');
        setUploadedCount(0);
        setTotalUploadCount(stagedFiles.length);
        const successFiles: string[] = [];
        const failedFiles: string[] = [];
        try {
            for (let i = 0; i < stagedFiles.length; i++) {
                const file = stagedFiles[i];
                setUploadedCount(i + 1);
                setCurrentUploadingFile(file.name);
                setUploadProgress(0);
                const result = await api.uploadFile(patientId, file, {
                    onProgress: (progress) => setUploadProgress(progress)
                });
                if (result.success) {
                    successFiles.push(file.name);
                } else {
                    failedFiles.push(file.name);
                    onUploadError?.(new Error(result.error ?? 'Unknown error'), {
                        fileName: file.name,
                        fileSize: file.size,
                        errorStack: result.errorStack
                    });
                }
            }
            await refreshAssets();
            if (failedFiles.length > 0) {
                setUploadError(
                    `${successFiles.length} file(s) uploaded. Failed: ${failedFiles.join(', ')}`
                );
            } else {
                setUploadSuccess(true);
                setStagedFiles([]);
                setTimeout(() => {
                    setShowUploadZone(false);
                    setUploadSuccess(false);
                }, 2000);
            }
        } catch {
            setUploadError('Failed to upload files. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
            setCurrentUploadingFile('');
            setUploadedCount(0);
            setTotalUploadCount(0);
        }
    }, [patientId, refreshAssets, stagedFiles, api, onUploadError]);

    return {
        showUploadZone,
        isDragOver,
        isUploading,
        uploadError,
        uploadSuccess,
        stagedFiles,
        uploadProgress,
        currentUploadingFile,
        uploadedCount,
        totalUploadCount,
        toggleUploadZone,
        setIsDragOver,
        handleDrop,
        handleFileInputChange,
        setStagedFiles,
        clearUploadError,
        uploadFiles
    };
}
